import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import { motion, AnimatePresence } from "framer-motion";

const heroImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_30eba26a-8d04-46fd-83fd-2d3d367cd47b/artifacts/zsi8mwys_download_1780878608688.jpg",
    alt: "제5회 경주힐링캠프 — 2026년 11월 5일~8일(3박 4일) 경주 더케이호텔, 40·50·60대 중년 싱글 남녀 80명",
    poster: true,
  },
  {
    url: "/images/hero-zoom-meeting.png",
    alt: "줌 미팅을 즐기는 한인 중년",
    headline: "화면 너머서도 인연은\n서로를 알아봅니다",
    title: "글로벌 줌미팅",
    subtitle: "안전한 검증 만남",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_62e113a6-fe02-48dd-a8bb-4b26ed6daf98/artifacts/onlg782c_Romantic%20sunset%20stroll%20by%20the%20beach.png",
    alt: "해변 일몰의 로맨틱한 커플",
    title: "AI가 당신의 글을 기반으로",
    subtitle: "잘 맞는 사람을 추천합니다",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_7210109a-68e5-4586-85d7-64b252b4fb1c/artifacts/getew5ma_ChatGPT%20Image%20May%2025%2C%202026%2C%2002_30_45%20PM.png",
    alt: "푸른 하늘 아래 샴페인 잔으로 건배하는 손",
    title: "오프라인 이벤트",
    subtitle: "“좋은 인연을 만나는 가장 우아한 방법”",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_7210109a-68e5-4586-85d7-64b252b4fb1c/artifacts/eyu980lj_ChatGPT%20Image%20May%2025%2C%202026%2C%2005_30_35%20PM.png",
    alt: "노을 진 호숫가에서 손을 잡고 있는 커플의 뒷모습",
    title: "지난 6년간 200여 쌍 탄생",
    boldTitle: true,
  },
  {
    url: "https://customer-assets.emergentagent.com/job_7210109a-68e5-4586-85d7-64b252b4fb1c/artifacts/0hvu96up_ChatGPT%20Image%20May%2025%2C%202026%2C%2003_03_47%20PM.png",
    alt: "에메랄드빛 바다 위 요트에서 휴식을 즐기는 여행",
    title: "1:1 맞춤매칭 리즈 연애정보회사",
    subtitle: "지구 어딘가에 있을 당신의 짝,\n리즈가 찾아드립니다",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_c31d3ebc-0781-4769-9553-55de188c1b77/artifacts/e3buzsnp_ChatGPT%20Image%20Mar%2018%2C%202026%2C%2007_49_51%20PM.png",
    alt: "야외 파티를 즐기는 50대 한인 남녀",
    title: "이제는 K-중년이 대세",
    subtitle: "글로벌 중년연애의 허브, 뉴 커플링",
  },
];

export const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToServices = () => {
    const element = document.querySelector("#services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goToCommunity = () => {
    navigate("/community");
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => {
          // 각 이미지별 최적 위치 설정 (머리가 안 잘리도록)
          const positions = [
            'center',      // 슬라이드 0: 이벤트 포스터 (contain)
            'center 30%',  // 슬라이드 1: 줌 미팅
            'center 35%',  // 슬라이드 2: 해변 일몰 커플
            'center 15%',  // 슬라이드 3: 숲속 포옹 커플
            'center 15%',  // 슬라이드 4: 해변 셀카 커플
            'center 25%',  // 슬라이드 5: 캔들라이트 디너
            'center 30%',  // 슬라이드 6: 야외 파티
          ];
          return (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out bg-no-repeat bg-center ${
                index === currentSlide ? "opacity-100" : "opacity-0"
              } ${image.poster ? "bg-contain md:bg-cover" : ""}`}
              style={{
                backgroundImage: `url('${image.url}')`,
                backgroundSize: image.poster ? undefined : 'cover',
                backgroundPosition: image.poster ? undefined : (positions[index] || 'center 25%'),
                backgroundColor: image.poster ? '#eef1f5' : undefined,
              }}
              data-testid={`hero-slide-${index}`}
            />
          );
        })}
        {/* Overlay - 약한 어두운 그라데이션 (포스터 슬라이드는 제외) */}
        {!heroImages[currentSlide].poster && (
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        )}
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? "bg-gold-400 w-8"
                : "bg-white/50 hover:bg-white/70"
            }`}
            data-testid={`slide-indicator-${index}`}
          />
        ))}
      </div>

      {/* Grain Overlay */}
      <div className="absolute inset-0 grain-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center flex flex-col items-center justify-center min-h-[60vh]">
        {/* Main Message - Animated per slide */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="font-sans text-white max-w-4xl mx-auto mb-24 leading-relaxed text-center"
            data-testid="hero-secondary"
          >
            {heroImages[currentSlide].poster ? null : heroImages[currentSlide].isLongform ? (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="font-light text-xl md:text-2xl lg:text-3xl mb-3 leading-snug text-white/90 space-y-1"
                  data-testid="hero-longform-title"
                >
                  {(heroImages[currentSlide].titleLines || [heroImages[currentSlide].title]).map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="font-bold text-3xl md:text-5xl lg:text-6xl text-white tracking-tight leading-tight max-w-3xl mx-auto"
                  data-testid="hero-longform-subtitle"
                >
                  {heroImages[currentSlide].subtitle}
                </motion.p>
              </>
            ) : heroImages[currentSlide].title === "K-중년이 대세" ? (
              <>
                <motion.p 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="font-light text-xl md:text-2xl lg:text-3xl text-white/90 mb-3"
                >
                  {heroImages[currentSlide].title}
                </motion.p>
                <motion.p 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="font-bold text-3xl md:text-5xl lg:text-6xl text-white tracking-tight leading-tight"
                >
                  {heroImages[currentSlide].subtitle}
                </motion.p>
              </>
            ) : heroImages[currentSlide].headline ? (
              <>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 }}
                  className="font-bold text-white text-4xl md:text-6xl lg:text-7xl tracking-tight leading-tight break-keep whitespace-pre-line mb-6"
                  data-testid="hero-headline"
                >
                  {heroImages[currentSlide].headline}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.35 }}
                  className="font-light text-white/90 text-2xl md:text-3xl lg:text-4xl break-keep"
                >
                  {heroImages[currentSlide].title}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="font-semibold text-white text-2xl md:text-3xl lg:text-4xl tracking-tight break-keep mt-1"
                >
                  {heroImages[currentSlide].subtitle}
                </motion.p>
              </>
            ) : (
              <>
                <motion.p 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className={`${heroImages[currentSlide].boldTitle ? "font-bold text-white text-5xl md:text-6xl lg:text-7xl" : "font-light text-white/90 text-4xl md:text-5xl lg:text-6xl"} mb-3 break-keep`}
                >
                  {heroImages[currentSlide].title}
                </motion.p>
                {heroImages[currentSlide].subtitle && (
                <motion.p 
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className={`${heroImages[currentSlide].boldTitle ? "font-light text-white/90" : "font-bold text-white"} text-3xl md:text-4xl xl:text-5xl tracking-tight leading-tight break-keep whitespace-pre-line`}
                >
                  {heroImages[currentSlide].subtitle}
                </motion.p>
                )}
                {heroImages[currentSlide].tagline && (
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="mt-5 font-serif italic text-lg md:text-xl lg:text-2xl text-white/80 tracking-wide text-center"
                    data-testid="hero-tagline-en"
                  >
                    {heroImages[currentSlide].tagline}
                  </motion.p>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* CTA Buttons (포스터 슬라이드에서는 숨김) */}
        {!heroImages[currentSlide].poster && (
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up delay-200"
          data-testid="hero-cta"
        >
          <Button
            onClick={() => {
              // Navigate to membership page
              navigate('/membership');
            }}
            className="btn-primary text-base px-8 py-6 min-w-[200px]"
            data-testid="hero-primary-cta"
          >
            회원가입 하기
          </Button>
          <Button
            onClick={goToCommunity}
            variant="outline"
            className="bg-transparent text-white border-2 border-white/50 rounded-full px-8 py-6 min-w-[200px] hover:bg-white/10 transition-colors duration-300"
            data-testid="hero-secondary-cta"
          >
            커뮤니티 둘러보기
          </Button>
        </div>
        )}
      </div>

      {/* Scroll Indicator */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer z-20"
        onClick={scrollToServices}
        data-testid="scroll-indicator"
      >
        <ChevronDown className="w-8 h-8 text-white/60" />
      </div>
    </section>
  );
};
